import { formatDate } from "date-fns";
import { useTranslation } from "react-i18next";
import { Checkbox, DropdownItem, Icon, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components";
import "components/FileDropdown/FileDropdown.scss";
import { getAttachmentIcon } from "modules/AppointmentDetails/utils";
import { getNameFromURL } from "utils/common";

import type { CheckboxProps } from "semantic-ui-react";

export type Attachment = {
  url: string;
  id?: number;
  name?: string;
  username?: string;
  updated_on?: string;
  external_source?: string;
};

type AttachmentsProps = {
  attachments: Attachment[];
  selectedFileUrls: string[];
  fromAPI?: boolean;
  setSelectedFileUrls: React.Dispatch<React.SetStateAction<string[]>>;
  handlePreviewAttachment: (evt: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => void;
};

type UploadInfoProps = {
  uploadedBy: string;
  uploadedOn: string;
};

const UploadInfo = ({ uploadedBy, uploadedOn }: UploadInfoProps) => {
  const { t } = useTranslation();

  if (uploadedBy && uploadedOn) return <span className="upload-info">{`${t("v8_uploaded_by", "Uploaded by")} ${uploadedBy} ${t("v8_on", "on")} ${uploadedOn}`}</span>;
  if (uploadedBy) return <span className="upload-info">{`${t("v8_uploaded_by", "Uploaded by")} ${uploadedBy}`}</span>;
  if (uploadedOn) return <span className="upload-info">{`${t("v8_uploaded_on", "Uploaded on")} ${uploadedOn}`}</span>;

  return null;
};

export const Attachments = ({ attachments, fromAPI, selectedFileUrls, setSelectedFileUrls, handlePreviewAttachment }: AttachmentsProps) => {
  const { t } = useTranslation();

  const getUploadedBy = (attachment: Attachment) => {
    if (attachment.external_source) return attachment.external_source;
    if (attachment.username) return attachment.username;
    if (fromAPI) return t("v8_public_api", "Public API");

    return "";
  };

  const handleSelectedFilesCheckboxChange = (e: React.FormEvent<HTMLInputElement>, { value }: CheckboxProps) => {
    e.stopPropagation();
    if (typeof value !== "string") return;

    setSelectedFileUrls(selectedFileUrls.includes(value) ? selectedFileUrls.filter(url => url !== value) : [...selectedFileUrls, value]);
  };

  return attachments.map((attachment, index) => {
    const fileName = attachment.name || getNameFromURL(attachment.url);
    const uploadedBy = getUploadedBy(attachment);
    const uploadedOn = attachment.updated_on ? formatDate(attachment.updated_on, DATE_FORMATS_FNS.dateMonthYearTime) : "";

    return (
      <DropdownItem key={attachment.url} onClick={evt => handlePreviewAttachment(evt, index)}>
        <div className="dropdown-item-row">
          <div className="dropdown-item-name-container">
            <Checkbox
              value={attachment.url}
              className="files-url-checkbox"
              onChange={handleSelectedFilesCheckboxChange}
              checked={selectedFileUrls.includes(attachment.url)}
            />

            <Icon className={getAttachmentIcon(fileName)} />
            <Popup trigger={<span className="dropdown-title ellipsis">{fileName}</span>} content={fileName} />
          </div>

          <div className="dropdown-info">
            <UploadInfo uploadedBy={uploadedBy} uploadedOn={uploadedOn} />
          </div>
        </div>
      </DropdownItem>
    );
  });
};
